import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queueApi from '../services/api';
import signalRService from '../services/signalR';
import { useLanguage } from '../contexts/LanguageContext';
import './PhoneNumberEntry.css';
import phoneIcon from '../images/phoneEntry.svg';
import Spinner from './Spinner';

const PhoneNumberEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getText } = useLanguage();
  const service = location.state?.service;

  if (!service) {
    console.error("No service provided, redirecting to services page");
    navigate("/");
    return null;
  }

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [countdown, setCountdown] = useState(25);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  const isComplete = phone.length === 9;

  const handleNumpadClick = (num) => {
    if (phone.length < 9) {
      setPhone(prev => prev + num.toString());
      setError(false);
      setLastInteraction(Date.now());
      setCountdown(25);
    }
  };

  const handleDelete = () => {
    setPhone(prev => prev.slice(0, -1));
    setError(false);
    setLastInteraction(Date.now());
    setCountdown(25);
  };

  useEffect(() => {
    if (loading) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const timeSinceLastInteraction = (now - lastInteraction) / 1000;
      
      if (timeSinceLastInteraction >= 25) {
        clearInterval(timer);
        navigate('/');
        return;
      }
      
      const remaining = Math.max(0, 25 - Math.floor(timeSinceLastInteraction));
      setCountdown(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, [lastInteraction, loading, navigate]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (loading) return;
      
      const key = event.key;
      
      if (key >= '0' && key <= '9') {
        handleNumpadClick(parseInt(key));
      }
      else if (key === 'Backspace' || key === 'Delete') {
        handleDelete();
      }
      else if (key === 'Enter' && isComplete) {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [loading, isComplete]);

  const handleSubmit = async () => {
    if (phone.length === 9) {
      setLoading(true);
      setError(false);
      try {
        const response = await queueApi.submitPhoneNumber(phone, service?.terminalServiceId);

        if (service?.isPhone && response?.clientTypeId && response?.clientTypeName) {
          queueApi.updateClientTypeCacheFromQueue(response);
        }

        if (response && response.isExisting) {
          signalRService.refreshQueue();
          navigate('/already', { state: { phone, queueData: response.data } });
          return;
        }

        if (response) {
          signalRService.refreshQueue();
          navigate('/create', { state: { queueData: response } });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "";
        if (errorMessage.includes("not found")) {
          setError(true);
        } else if (errorMessage.includes("no products")) {
          navigate('/product-not-found', { state: { phone } });
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    }
  };


  return (
      <div className="phone-page-wrapper">
        {loading && <Spinner />}
        <main className="phone-main-content">

          <div className="input-side">
            <h2 className="entry-title">{getText('Введите номер телефона', 'Рақами телефонро ворид кунед')}</h2>

            <div className={`phone-input-custom ${error ? 'error-border' : ''}`}>
              <div className="icon-container">
                <img src={phoneIcon} alt="phone" className="phone-svg" />
              </div>
              <div className="number-display">
                <span className="prefix">+992</span>
                <span className={`digits ${phone ? 'filled' : 'placeholder'}`}>
                {phone ? phone : '--- -- -- --'}
              </span>
              </div>
            </div>
            

            {!error ? (
                <p className="instruction-text1">
                  {getText('Пожалуйста, укажите ваш номер для получения очереди', 'Лутфан, рақами телефони худро барои гирифтани навбат ворид кунед')}
                </p>
            ) : (
                <div className="error-container-frame">
                  <div className="error-badge-main">
                    <div className="error-icon-circle">!</div>
                    <span className="error-text-title">{getText('Неверный номер!', 'Рақами нодуруст!')}</span>
                  </div>
                  <p className="error-hint-text">{getText('Пожалуйста, введите свой номер телефона правильно.', 'Лутфан, рақами телефони худро дуруст ворид кунед.')}</p>
                </div>
            )}
          </div>

          {/* Қисмати рост: Numpad */}
          <div className="numpad-side">
            <div className="numpad-white-card">
              <div className="numpad-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <button key={num} className="numpad-key" onClick={() => handleNumpadClick(num)} disabled={loading}>
                      {num}
                    </button>
                ))}
                <div />
                <button className="numpad-key" onClick={() => handleNumpadClick(0)} disabled={loading}>0</button>
                <button className="numpad-key delete-key" onClick={handleDelete} disabled={loading}>
                  <svg width="41" height="31" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                    <line x1="18" y1="9" x2="12" y2="15" />
                    <line x1="12" y1="9" x2="18" y2="15" />
                  </svg>
                </button>
              </div>
            </div>

            <button
                className={`submit-phone-btn ${isComplete ? 'active-state' : 'disabled-state'} ${loading ? 'loading-state' : ''}`}
                disabled={!isComplete || loading}
                onClick={handleSubmit}
            >
              {loading ? (
                <span className="spinner"></span>
              ) : getText('Получить очередь', 'Гирифтани навбат')}
            </button>
          </div>
        </main>
      </div>
  );
};

export default PhoneNumberEntry;